import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import {
  getLocalizedText,
  setLocale,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  DisplayText,
  Loading,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import {
  StyledAuthBody,
  StyledAuthContent,
  StyledAuthFrame,
  StyledAuthLogo,
  StyledAuthMain,
  StyledAuthMedia,
  StyledAuthMediaWrap,
} from "@rentiohq/web-shared/dist/scenes/Auth/Auth.styled";
import { ThemeProvider } from "@rentiohq/web-theme";
import React from "react";
import logoRentmatch from "../../assets/svg/logo-rentmatch.svg";
import {
  EExternalActionBrand,
  getExternalActionBrand,
  withRentmatchPrimary,
} from "./externalAction.brand";
import {
  EExternalActionState,
  followsRedirectAfterPress,
  getExternalActionCopy,
  IExternalActionState,
  isAllowedRedirect,
} from "./externalAction.copy";
import * as S from "./ExternalAction.styles";

// INFO: the page every external-action link in a mail opens (`/external-action#id=&key=`).
// Public: the reader is a candidate, an owner or a tenant who may never have signed in to
// the dashboard.
//
// Opening it changes nothing. Mail scanners open every url in a mail, so the page only reads
// what the link is about, and the action runs on the button. The id and key are the link's
// credential, so they are taken from the address bar once and removed from it, and the page
// sends no referrer (DEV-3330).

// INFO: the languages the page holds. Anything else in the url is ignored rather than
// trusted.
const SUPPORTED_LANGUAGE = /^(nl|fr|en)([_-][a-z]{2})?$/i;

// INFO: the link as first read, for the whole life of the page. The address bar is cleared
// right after, and the dashboard can mount this page again on its way up, which would then
// read an empty url and call a working link dead.
let capturedLink: { id: string; key: string } | undefined;

const readLinkFromUrl = () => {
  // The fragment is where links put the id and key: a browser never sends it to a server or
  // in a referrer, and analytics leaves it out of the page url. The query string is still read
  // for links minted before the fragment.
  const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const fromQuery = new URLSearchParams(window.location.search);
  const read = (name: string) =>
    fromHash.get(name) || fromQuery.get(name) || "";

  const id = read("id");
  const key = read("key");
  const lang = read("lang");

  if (SUPPORTED_LANGUAGE.test(lang)) {
    setLocale(lang);
  }

  if (id && key) {
    capturedLink = { id, key };
  }

  return capturedLink ?? { id: "", key: "" };
};

// INFO: straight to the API with plain axios: an expired link answers 401, and the regular
// client would treat that as the signed-in user's session expiring and sign them out.
const externalActionUrl = (params: { id: string; key: string }) =>
  `${CONFIG.BASE_URL_V2}/external-action/${encodeURIComponent(
    params.id,
  )}/${encodeURIComponent(params.key)}`;

// INFO: api-next answers in its v2 envelope, `{ data: ... }`. Read without unwrapping, every
// field is undefined and a working link reads as dead.
const unwrap = <T,>(body: unknown): T | undefined => {
  if (body && typeof body === "object" && "data" in body) {
    return (body as { data: T }).data;
  }

  return body as T | undefined;
};

type TPageState = "loading" | "open" | "done" | "unavailable";

export const ExternalAction = () => {
  const [link] = React.useState(readLinkFromUrl);

  const [pageState, setPageState] = React.useState<TPageState>("loading");
  const [externalAction, setExternalAction] =
    React.useState<IExternalActionState>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string>();

  React.useEffect(() => {
    // Out of the address bar, the browser history and anything that reports page urls.
    // In an effect rather than where the url is read: StrictMode reads it twice.
    window.history.replaceState(null, "", window.location.pathname);

    const referrer = document.createElement("meta");
    referrer.name = "referrer";
    referrer.content = "no-referrer";
    document.head.appendChild(referrer);

    return () => {
      document.head.removeChild(referrer);
    };
  }, []);

  React.useEffect(() => {
    if (!link.id || !link.key) {
      setPageState("unavailable");
      return;
    }

    let isCancelled = false;

    api
      .get<IExternalActionState>(`${externalActionUrl(link)}/state`, {
        isFullUrl: true,
        authenticated: false,
      })
      .then(response => {
        if (isCancelled) {
          return;
        }

        const result = unwrap<IExternalActionState>(response.data);
        if (!result || !getExternalActionCopy(result)) {
          setPageState("unavailable");
          return;
        }

        setExternalAction(result);
        switch (result.state) {
          case EExternalActionState.Open:
            setPageState("open");
            break;

          case EExternalActionState.Done:
            setPageState("done");
            break;

          default:
            setPageState("unavailable");
        }
      })
      .catch(() => {
        // Expired, unknown or unreachable: there is nothing for the reader to do with it
        // either way, so all three read as a link that no longer works.
        if (!isCancelled) {
          setPageState("unavailable");
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [link]);

  const handlePress = async () => {
    if (!externalAction || isSubmitting) {
      return;
    }

    setError(undefined);
    setIsSubmitting(true);
    try {
      const response = await api.post<{ redirectUrl?: string }>(
        externalActionUrl(link),
        { isFullUrl: true, authenticated: false, data: {} },
      );

      const redirectUrl = unwrap<{ redirectUrl?: string }>(response.data)
        ?.redirectUrl;
      if (
        redirectUrl &&
        followsRedirectAfterPress(externalAction.actionType) &&
        isAllowedRedirect(redirectUrl)
      ) {
        window.location.href = redirectUrl;
        return;
      }

      setPageState("done");
    } catch (unknownError) {
      // Never a thank-you on a failure: the reader would wait for an answer to something
      // nobody received.
      setError(getLocalizedText("external_action.failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const copy = externalAction
    ? getExternalActionCopy(externalAction)
    : undefined;

  const isRentmatch =
    getExternalActionBrand(externalAction?.actionType) ===
    EExternalActionBrand.Rentmatch;

  // No logo until the link is known: showing Rentio's first would flash the
  // wrong brand above a Rentmatch action.
  const renderLogo = () => {
    if (pageState === "loading") {
      return null;
    }

    return isRentmatch ? (
      <S.RentmatchLogo src={logoRentmatch} alt="Rentmatch" />
    ) : (
      <StyledAuthLogo />
    );
  };

  const renderContent = () => {
    if (pageState === "loading") {
      return <Loading />;
    }

    if (pageState === "unavailable" || !copy) {
      return (
        <S.Body>
          <TextStyle variation="subdued">
            {getLocalizedText("external_action.unavailable")}
          </TextStyle>
        </S.Body>
      );
    }

    if (pageState === "done") {
      return <DisplayText>{copy.done}</DisplayText>;
    }

    const button = (
      <Button
        appearance="primary"
        onClick={handlePress}
        isDisabled={isSubmitting}
        isSubmitting={isSubmitting}
      >
        {copy.button}
      </Button>
    );

    return (
      <>
        <DisplayText>{copy.title}</DisplayText>
        {copy.address && (
          <S.Address>
            <TextStyle variation="strong">{copy.address}</TextStyle>
          </S.Address>
        )}
        <S.Body>
          <TextStyle variation="subdued">{copy.body}</TextStyle>
        </S.Body>
        {error && (
          <S.Error>
            <TextStyle variation="negative">{error}</TextStyle>
          </S.Error>
        )}
        <S.Actions>
          {/* The page's own button, only recolored for a Rentmatch action, so
              a Rentio action renders exactly as before. */}
          {isRentmatch ? (
            <ThemeProvider theme={withRentmatchPrimary}>{button}</ThemeProvider>
          ) : (
            button
          )}
        </S.Actions>
      </>
    );
  };

  return (
    <StyledAuthFrame>
      <StyledAuthContent>
        <StyledAuthMain>
          {renderLogo()}
          <StyledAuthBody>{renderContent()}</StyledAuthBody>
        </StyledAuthMain>
      </StyledAuthContent>
      <StyledAuthMediaWrap>
        <StyledAuthMedia />
      </StyledAuthMediaWrap>
    </StyledAuthFrame>
  );
};

// eslint-disable-next-line import/no-default-export
export default ExternalAction;
